import { useQuery } from '@tanstack/react-query';
import {
  fetchAllGroupMemberUsers,
  fetchSnapshotNames,
  type MemberLookupEntry,
} from '@/api/groups';

/**
 * Provides a lookup map for ALL group members (including former/soft-deleted).
 * Uses a 2-layer resolution strategy:
 *   1. group_members → users join (works for most cases)
 *   2. expense_splits.participant_display_name snapshot (fallback for RLS-blocked users)
 */
export function useGroupMemberLookup(groupId: string | undefined) {
  return useQuery({
    queryKey: ['member-lookup', groupId],
    queryFn: async (): Promise<Map<string, MemberLookupEntry>> => {
      const { resolved, unresolvedIds } = await fetchAllGroupMemberUsers(groupId!);

      if (unresolvedIds.length > 0) {
        const snapshots = await fetchSnapshotNames(groupId!, unresolvedIds);
        for (const [userId, name] of snapshots) {
          resolved.set(userId, {
            displayName: name,
            avatarUrl: null,
            isFormer: true,
          });
        }
      }

      return resolved;
    },
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000,
  });
}
